import { ReadonlyVec2, ReadonlyVec3 } from 'gl-matrix';
import { syncCanvasSizeToLayout } from '~/engine/core/render/overlayCanvas';
import { PlaneView } from '~/engine/view/views/PlaneView';

/**
 * Lightweight brush cursor that draws a circle on the PlaneView's 2D
 * canvas overlay. Shared by the SDF-native brush tools (deform,
 * smoothing brush) — much cheaper than a GL scene object for a cursor
 * that only ever needs an outline.
 */
export class CanvasCircleCursor {
    private diameterMm: number;
    private readonly color: ReadonlyVec3;
    private lastView?: PlaneView;

    public constructor(diameterMm: number, color: ReadonlyVec3) {
        this.diameterMm = diameterMm;
        this.color = color;
    }

    public setDiameter(diameterMm: number): void {
        this.diameterMm = diameterMm;
    }

    public draw(view: PlaneView, mouseCoords: ReadonlyVec2): void {
        if (this.lastView && this.lastView !== view) {
            this.clearCanvas(this.lastView);
        }
        this.lastView = view;

        const canvas = view.get2DCanvas;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        syncCanvasSizeToLayout(ctx);
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        const patientPos = view.clientToPatientCoordinates(mouseCoords);
        const canvasCenter = view.canvasCoordsFromPatientCoords(patientPos);

        const mmPerPixel = view.resolution[0];
        const radiusPx = mmPerPixel > 0 ? this.diameterMm / 2 / mmPerPixel : 0;

        const r = Math.round(this.color[0] * 255);
        const g = Math.round(this.color[1] * 255);
        const b = Math.round(this.color[2] * 255);

        ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(canvasCenter[0], canvasCenter[1], radiusPx, 0, 2 * Math.PI);
        ctx.stroke();
    }

    public clear(): void {
        if (this.lastView) {
            this.clearCanvas(this.lastView);
            this.lastView = undefined;
        }
    }

    private clearCanvas(view: PlaneView): void {
        const canvas = view.get2DCanvas;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        }
    }
}
